import { Theme, css } from '@emotion/react';

export const MOBILE_HEADER_HEIGHT = 68;
export const DESKTOP_HEADER_HEIGHT = 84;

const desktopSlimPadding = '16px';

export const desktopHeaderSlimLeft = css`
  padding-left: ${desktopSlimPadding};
`;

export const desktopHeaderSlimRight = css`
  padding-right: ${desktopSlimPadding};
`;

const mobileSlimPadding = '8px';

export const mobileHeaderSlimLeft = css`
  padding-left: ${mobileSlimPadding};
`;

export const mobileHeaderSlimRight = css`
  padding-right: ${mobileSlimPadding};
`;

/*
 * The flex-shrink: 0 below makes sure that the header size isn't reduced below the
 * header's designated height even if the header is presented in a flex layout.
 */
const omakaseHeaderCommon = ({ colors }: Theme) => css`
  background-color: ${colors.bgDefault};
  flex-shrink: 0;
  justify-content: space-between;
  position: sticky;
  top: 0;
  width: 100%;
`;

interface ShadowOptions {
  showShadow: boolean;
}

export const omakaseHeaderShadow = (
  { shadow, timings }: Theme,
  { showShadow }: ShadowOptions
) => css`
  box-shadow: ${showShadow ? shadow[100] : 'none'};
  transition: box-shadow ${timings.duration.standard} ${timings.timingFunction.easeInOut};
`;

export const omakaseHeaderMobileContainer = (theme: Theme) => css`
  ${omakaseHeaderCommon(theme)};
  display: flex;
  flex-direction: column;
  ${theme.breakpoints.up('md')} {
    display: none;
  }
`;

export const omakaseHeaderMobileMain = css`
  display: flex;
  min-height: ${MOBILE_HEADER_HEIGHT}px;
  padding: 0 20px;
  justify-content: space-between;
  position: relative;
`;

export const omakaseHeaderDesktopContainer = (theme: Theme) => css`
  ${omakaseHeaderCommon(theme)};
  display: none;

  ${theme.breakpoints.up('md')} {
    display: flex;
    flex-direction: column;
  }
`;

export const omakaseHeaderDesktopMain = (theme: Theme) => css`
  display: flex;
  height: ${DESKTOP_HEADER_HEIGHT}px;
  padding: 0 28px;
  justify-content: space-between;
  position: relative;
`;

const omakaseHeaderItems = css`
  align-items: center;
  display: flex;
  white-space: nowrap;
`;

export const omakaseHeaderLeftItems = css`
  ${omakaseHeaderItems};
  overflow: visible;
`;

export const omakaseHeaderLeftmostItemDivider = (
  { breakpoints, colors, spacing }: Theme,
  shrinkMarginsAtMediumWidth: boolean,
  slimRightMargin: boolean
) => {
  // Usually, we put spacing(5) on either side of the divider, but in some cases we
  // may have less room to work with and at only medium screen widths we stick
  // with spacing(3) instead.
  //
  // We further reduce this by another 3 spacing units on the right margin if
  // the content to the right of the divider has its own spacing.
  const fullSpacing = css`
    margin: 0 ${spacing(slimRightMargin ? 2 : 5)}px 0 ${spacing(5)}px;
  `;

  return [
    css`
      border-left: 1px solid ${colors.borderDefault};
      height: ${spacing(3)}px;
    `,
    shrinkMarginsAtMediumWidth
      ? css`
          margin: 0 ${slimRightMargin ? 0 : spacing(3) + 'px'} 0 ${spacing(3)}px;
          ${breakpoints.up('md')} {
            ${fullSpacing};
          }
        `
      : fullSpacing,
  ];
};

export const omakaseHeaderCenterItems = css`
  ${omakaseHeaderItems};
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;

  * {
    margin: auto;
  }
`;

export const omakaseHeaderRightItems = css`
  ${omakaseHeaderLeftItems};
  justify-content: flex-end;
  & > * + * {
    margin-left: 8px;
  }
`;

export const omakaseHeaderAuthenticationButtons = ({ spacing }: Theme) => css`
  & > a:not(:first-of-type) {
    width: 127px;
    margin-left: ${spacing(1)}px;
  }
`;
